import { useState } from 'react';
import Modal from '../Modal';
import schedulingService, { type AppointmentSummary } from '../../services/scheduling.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';

interface Props {
  appointment: AppointmentSummary;
  onClose: () => void;
  onCancelled: (updated: AppointmentSummary) => void;
}

const MAX_REASON = 500;

/**
 * Confirm-and-reason modal for cancelling a scheduled appointment.
 * Used by staff (agent / manager). Backend enforces:
 *   - members can only cancel ≥2h before start (not used from staff page)
 *   - any agent or manager may cancel any time
 * The reason is optional and goes into `appointments.cancel_reason` for
 * the audit trail.
 */
export default function CancelAppointmentModal({
  appointment,
  onClose,
  onCancelled,
}: Readonly<Props>): JSX.Element {
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const updated = await schedulingService.cancelAppointment(
        appointment.id, reason.trim() || null,
      );
      onCancelled(updated);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not cancel this appointment.'));
    } finally {
      setSubmitting(false);
    }
  }

  const startsLabel = (() => {
    try {
      return new Date(appointment.starts_at).toLocaleString(readerLocale(), {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return appointment.starts_at;
    }
  })();

  return (
    <Modal open onClose={onClose} busy={submitting} maxWidth="md">
      <h2 id="cancel-appt-title" className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Cancel appointment
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        The member&apos;s slot will be released. They&apos;ll see the cancelled state on
        their case page.
      </p>

      <div className="rounded-lg p-3 mb-4 text-sm space-y-1"
           style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}>
        <div><span style={{ color: colors.mutedSoft }}>Member:</span>{' '}
          <span style={{ color: colors.ink }}>{fullName(appointment.member)}</span></div>
        <div><span style={{ color: colors.mutedSoft }}>When:</span>{' '}
          <span style={{ color: colors.ink }}>{startsLabel}</span></div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block text-sm">
          <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
            Reason{' '}
            <span className="font-normal" style={{ color: colors.mutedSoft }}>
              (optional, max {MAX_REASON} chars)
            </span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={MAX_REASON}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
            placeholder="Goes to the audit log."
          />
        </label>

        {error && (
          <div className="rounded-lg p-3 text-sm" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={submitting}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
            Keep it
          </button>
          <button type="submit" disabled={submitting}
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                  style={{
                    background: colors.danger, color: '#FFFFFF',
                    opacity: submitting ? 0.6 : 1,
                    cursor: submitting ? 'wait' : 'pointer',
                  }}>
            {submitting ? 'Cancelling…' : 'Cancel appointment'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
